// @flow
import React from 'react'
import classNames from 'classnames'
import { isEmpty } from 'ramda'

import { RadioTile } from '../../../Components/'

import AbstractFormField from './AbstractFormField'
import FieldError from '../form/Error'

// types

type TilesProps = {
  domain: {
    _type: string,
    values: Array<string>,
  },
  collapse?: number,
  enabled?: boolean,
  icons?: { [option: string]: string },
  id: string,
  isPristine?: boolean,
  labelCollapsed?: string,
  name: string,
  onChange: (value: string) => void,
  prefix?: string,
  textMode?: boolean,
  value: string,
  valueNames?: { [key: string]: string },
}

type SelectedValue = {
  [key: string]: string,
}

type TilesState = {
  collapsedAfterIndex: boolean | number,
  isCollapsed: boolean,
  labels: { [key: string]: string },
  options: Array<SelectedValue>,
  pristine: boolean,
}

/**
 * Component for rendering tiles
 * @example ./docs/Tiles.md
 */

// TODO potential adoption functional component solution to minimize `getDerivedStateFromProps` actions
export default class Tiles extends AbstractFormField<TilesProps, TilesState> {
  static defaultProps = {
    enabled: true,
    isPristine: false,
    prefix: '',
    textMode: false,
  }

  static getDerivedStateFromProps(nextProps: TilesProps, prevState: TilesState) {
    let labels = {}
    let options = []
    let state = {}

    if (nextProps.valueNames) {
      labels = Object.keys(nextProps.valueNames).reduce((acc, value) => {
        acc[value] = nextProps.valueNames[value]
        options.push({ value, label: acc[value] })
        return acc
      }, {})
    } else {
      labels = nextProps.domain.values.reduce((acc, value) => {
        acc[value] = value
        options.push({ value, label: value })
        return acc
      }, {})
    }

    let collapsedAfterIndex = prevState.collapsedAfterIndex
    if (prevState.collapsedAfterIndex === false && nextProps.collapse) {
      collapsedAfterIndex = nextProps.collapse
    }

    if (
      !nextProps.isPristine !== !prevState.pristine &&
      nextProps.isPristine !== prevState.prevIsPristine &&
      !nextProps.isPristine
    ) {
      state.pristine = false
      state.prevIsPristine = nextProps.isPristine
    }

    return {
      labels,
      options,
      collapsedAfterIndex,
      ...state,
    }
  }

  constructor(props: TilesProps) {
    super(props)
    this.state = {
      options: [],
      prevPropsValue: '',
      pristine: true,
      collapsedAfterIndex: false,
      isCollapsed: true,
      labels: {},
    }
  }

  handleOnchange = (value: string) => {
    this.props.onChange(value)
  }

  handleShowCollapsed = () => {
    this.setState({ isCollapsed: false })
  }

  render() {
    const {
      textMode,
      value,
      icons,
      id,
      prefix,
      name,
      enabled,
      labelCollapsed,
      templateVariables,
      validationErrors,
    } = this.props
    const { collapsedAfterIndex, isCollapsed } = this.state

    if (textMode || !enabled) {
      return <span>{this.state.labels ? this.state.labels[value] : value}</span>
    }

    const error = !this.isPristine() && validationErrors && !isEmpty(validationErrors[name]) && validationErrors[name]
    const classes = ['row', error && 'is-invalid'] // TODO: handle errors

    return (
      <div className={classNames(classes)}>
        {this.state.options.map((option, index) => {
          if (
            isCollapsed &&
            collapsedAfterIndex &&
            index >= collapsedAfterIndex &&
            collapsedAfterIndex &&
            option.value !== value
          ) {
            return null
          }

          if (!enabled && option.value !== value) {
            return null
          }

          return (
            <RadioTile
              checked={value === option.value}
              enabled={enabled}
              icon={icons ? icons[option.value] : ''}
              id={`${prefix}-${id}-${index}`}
              key={`${id}-${index}`}
              label={option.label}
              name={`${prefix}${name}`}
              onChange={this.handleOnchange}
              value={option.value}
            />
          )
        })}
        {collapsedAfterIndex !== false && isCollapsed && enabled && (
          <RadioTile
            checked={false}
            enabled
            iconLabel="+"
            id={`${name}-showMore`}
            label={labelCollapsed || ''}
            name={`${name}-showMore`}
            onChange={this.handleShowCollapsed}
            value="showMore"
          />
        )}
        <FieldError
          error={error}
          name={name}
          onHideError={this.hideError}
          prefix={prefix}
          templateVariables={templateVariables}
        />
      </div>
    )
  }
}
